import { useEffect, useMemo, useState } from 'react';
import TimeDropdown from './TimeDropdown';
import {
  DayItem,
  DayKey,
  Schedule,
  ScheduleInterval,
  WorkSchedulerProps,
} from './WorkSchedulerTypes';
import './WorkScheduler.css';

const DAYS: DayItem[] = [
  { key: 'sun', short: 'Su', label: 'Sunday' },
  { key: 'mon', short: 'Mo', label: 'Monday' },
  { key: 'tue', short: 'Tu', label: 'Tuesday' },
  { key: 'wed', short: 'We', label: 'Wednesday' },
  { key: 'thu', short: 'Th', label: 'Thursday' },
  { key: 'fri', short: 'Fr', label: 'Friday' },
  { key: 'sat', short: 'Sa', label: 'Saturday' },
];

const DEFAULT_ACTIVE_DAYS: DayKey[] = ['mon', 'tue', 'wed', 'thu', 'fri'];

const makeInterval = (showEndTime: boolean): ScheduleInterval =>
  showEndTime ? { start: '09:00', end: '17:00' } : { start: '09:00' };

const buildDefaultSchedule = (showEndTime: boolean): Schedule => ({
  mon: [makeInterval(showEndTime)],
  tue: [makeInterval(showEndTime)],
  wed: [makeInterval(showEndTime)],
  thu: [makeInterval(showEndTime)],
  fri: [makeInterval(showEndTime)],
});

function WorkScheduler({
  schedule,
  setSchedule,
  isDisabled = false,
  showEndTime = true,
}: WorkSchedulerProps) {
  const defaultSchedule = useMemo(() => buildDefaultSchedule(showEndTime), [showEndTime]);

  const currentSchedule: Schedule = schedule ?? defaultSchedule;

  const [activeDays, setActiveDays] = useState<DayKey[]>(
    schedule ? (Object.keys(schedule) as DayKey[]) : DEFAULT_ACTIVE_DAYS,
  );

  useEffect(() => {
    if (schedule) {
      setActiveDays(Object.keys(schedule) as DayKey[]);
    }
  }, [schedule]);

  const toggleDay = (dayKey: DayKey) => {
    if (isDisabled) return;

    const isActive = activeDays.includes(dayKey);
    const next = isActive ? activeDays.filter((d) => d !== dayKey) : [...activeDays, dayKey];

    setActiveDays(next);

    const newSchedule: Schedule = {};
    next.forEach((key) => {
      newSchedule[key] = currentSchedule[key] || [makeInterval(showEndTime)];
    });

    setSchedule(newSchedule);
  };

  const updateInterval = (dayKey: DayKey, idx: number, field: 'start' | 'end', value: string) => {
    const updatedDay = [...(currentSchedule[dayKey] || [])];
    const currentInterval = updatedDay[idx] || makeInterval(showEndTime);

    updatedDay[idx] = {
      ...currentInterval,
      [field]: value,
    };

    if (!showEndTime) {
      delete updatedDay[idx].end;
    }

    setSchedule({
      ...currentSchedule,
      [dayKey]: updatedDay,
    });
  };

  const addInterval = (dayKey: DayKey) => {
    const updatedDay = [...(currentSchedule[dayKey] || []), makeInterval(showEndTime)];

    setSchedule({
      ...currentSchedule,
      [dayKey]: updatedDay,
    });
  };

  const removeInterval = (dayKey: DayKey, idx: number) => {
    const updatedDay = (currentSchedule[dayKey] || []).filter((_, i) => i !== idx);

    setSchedule({
      ...currentSchedule,
      [dayKey]: updatedDay,
    });
  };

  return (
    <>
      {/* Day pills */}
      <div className="ws-day-pills">
        {DAYS.map((day) => {
          const isActive = activeDays.includes(day.key);

          return (
            <button
              key={day.key}
              className={`ws-day-pill ${isActive ? 'ws-day-pill--active' : ''}`}
              onClick={() => toggleDay(day.key)}
              disabled={isDisabled}
              type="button"
            >
              {day.short}
            </button>
          );
        })}
      </div>

      {/* Schedule rows */}
      <div className="ws-schedule-rows">
        {DAYS.filter((day) => activeDays.includes(day.key)).map((day) => {
          const intervals = currentSchedule[day.key] || [makeInterval(showEndTime)];

          return (
            <div key={day.key} className="ws-day-row">
              <span className="ws-day-label">{day.label}</span>

              <div className="ws-intervals">
                {intervals.map((interval, idx) => (
                  <div key={idx} className="ws-interval">
                    <TimeDropdown
                      value={interval.start}
                      onChange={(val) => updateInterval(day.key, idx, 'start', val)}
                      disabled={isDisabled}
                    />

                    {showEndTime && (
                      <>
                        <span className="ws-dash">—</span>
                        <TimeDropdown
                          value={interval.end || '17:00'}
                          onChange={(val) => updateInterval(day.key, idx, 'end', val)}
                          disabled={isDisabled}
                        />
                      </>
                    )}

                    {intervals.length > 1 && (
                      <button
                        className="ws-remove-btn"
                        onClick={() => removeInterval(day.key, idx)}
                        disabled={isDisabled}
                        type="button"
                        title="Remove interval"
                      >
                        <i className="fa-solid fa-minus" />
                      </button>
                    )}
                  </div>
                ))}
              </div>

              <button
                className="ws-add-btn"
                onClick={() => addInterval(day.key)}
                disabled={isDisabled}
                type="button"
                title="Add interval"
              >
                <i className="fa-solid fa-circle-plus" />
              </button>
            </div>
          );
        })}
      </div>
    </>
  );
}

export default WorkScheduler;
